// useDateTimeRange.js
import { ref, computed } from "vue";
import { useDateTimeRangeValidation } from "./use-date-range-validation";
import { dateToDateTimeString, dateToIsoDateString, dateTimeStringToDate, isoDateStringToDate } from "@/js/general";

export const PREDEFINED_RANGES = [
  { value: 300, text: "Last 5 min", from: "now-5min", to: "now" },
  { value: 900, text: "Last 15 min", from: "now-15min", to: "now" },
  { value: 1800, text: "Last 30 min", from: "now-30min", to: "now" },
  { value: 3600, text: "Last 1 hour", from: "now-1h", to: "now" },
  { value: 21600, text: "Last 6 hours", from: "now-6h", to: "now" },
  { value: 43200, text: "Last 12 hours", from: "now-12h", to: "now" },
  { value: 86400, text: "Last 24 hours", from: "now-24h", to: "now" },
  { value: 172800, text: "Last 2 days", from: "now-48h", to: "now" },
  { value: 604800, text: "Last 7 days", from: "now-168h", to: "now" },
  { value: 1209600, text: "Last 14 days", from: "now-336h", to: "now" },
  { value: 2592000, text: "Last 30 days", from: "now-720h", to: "now" },
];

export function useDateTimeRange({ range, useStringFormat = false, defaultSeconds }) {
  const { 
    validateTimeExpression,
    expressionToDate,
    validateDateTimeString,
  } = useDateTimeRangeValidation();

  const formatRangeValue = (value) => {
    if (value instanceof Date) {
      return dateToDateTimeString(value);
    }

    if (typeof value === "string" && (validateTimeExpression(value) || validateDateTimeString(value))) {
      return value;
    }

    return "";
  };
  
  // Input values
  const isCustomRange = ref(false);
  const _from = ref(formatRangeValue(range.value.from));
  const isValidFrom = ref(true);
  const fromInput = computed({
    get() {
      return _from.value;
    },
    set(value) {
      isValidFrom.value = validateTimeExpression(value) || validateDateTimeString(value);
      _from.value = value;
      if (isValidFrom.value) {
        updateFromValue();
        updatePickerDates();
      }
    }
  });
  const _to = ref(formatRangeValue(range.value.to));
  const isValidTo = ref(true);
  const toInput = computed({
    get() {
      return _to.value;
    },
    set(value) {
      isValidTo.value = validateTimeExpression(value) || validateDateTimeString(value);
      _to.value = value;
      if (isValidTo.value) {
        updateToValue();
        updatePickerDates();
      }
    }
  });


  // Determine predefined range
  const determinePredefinedRangeIdx = () => {
    if (range.value.to === "now" && validateTimeExpression(range.value.from)) {
      return PREDEFINED_RANGES.findIndex((i) => i.from === range.value.from);
    }
    const idx = PREDEFINED_RANGES.findIndex(r => r.value === range.value.seconds);
    return idx >= 0 ? idx : PREDEFINED_RANGES.findIndex(r => r.value === defaultSeconds);
  };
  
  const predefinedRangeIdx = ref(determinePredefinedRangeIdx());

  const pickerDates = ref(["", ""]);
  const updatePickerDates = () => {
    if (fromInput.value instanceof Date) {
      pickerDates.value.splice(0, 1, dateToIsoDateString(fromInput.value));
      pickerDates.value.splice(1, 1, dateToIsoDateString(toInput.value));
    } else if (validateTimeExpression(fromInput.value) && validateTimeExpression(toInput.value)) {
      pickerDates.value.splice(0, 1, dateToIsoDateString(expressionToDate(fromInput.value)));
      pickerDates.value.splice(1, 1, dateToIsoDateString(expressionToDate(toInput.value)));
    } else if (validateDateTimeString(fromInput.value) && validateDateTimeString(toInput.value)) {
      pickerDates.value.splice(0, 1, fromInput.value);
      pickerDates.value.splice(1, 1, toInput.value);
    }
  }
  updatePickerDates();
  
  // Update methods
  const updateFromValue = () => {
    if (!isValidFrom.value) {
      return;
    }

    const isValidTimeExpression = validateTimeExpression(fromInput.value); 
    if (useStringFormat && isValidTimeExpression) {
      range.value.from = fromInput.value;
      range.value.seconds = 0;
      return;
    }

    if (isValidTimeExpression) {
      const fromDate = expressionToDate(fromInput.value);
      const now = new Date();
      if (toInput.value === "now") {
        range.value.seconds = Math.trunc((now.getTime() - fromDate.getTime()) / 1000);
      } else {
        range.value.seconds = 0;
      }
      range.value.from = fromDate;
      return;
    }

    const date = dateTimeStringToDate(fromInput.value);
    range.value.from = date;
    range.value.seconds = 0;
  };
  
  const updateToValue = () => {
    if (!isValidTo.value) {
      return;
    }

    const isValidTimeExpression = validateTimeExpression(toInput.value); 
    if (useStringFormat && isValidTimeExpression) {
      range.value.to = toInput.value;
      range.value.seconds = 0;
      return;
    }

    if (isValidTimeExpression) {
      const toDate = expressionToDate(toInput.value);
      if (toInput.value === "now") {
        range.value.seconds = calculateSeconds(fromInput.value);
      } else {
        range.value.seconds = 0;
      }
      range.value.to = toDate;
      return;
    }
    
    const date = dateTimeStringToDate(toInput.value);
    range.value.to = date;
    range.value.seconds = 0;
  };

  const calculateSeconds = (from) => {
    let _d;
    const now = new Date();
    if (validateTimeExpression(from)) {
      _d = expressionToDate(from);
    } else if (validateDateTimeString(from)) {
      _d = dateTimeStringToDate(from);
    } else if (from instanceof Date) {
      _d = from;
    }

    return !_d ? 0 : Math.trunc((now.getTime() - _d.getTime()) / 1000);
  }
  
  // Handle picker input
  const handlePickerInput = (value) => {
    range.value.from = isoDateStringToDate(value[0]);
    const to = isoDateStringToDate(value[1]);
    to.setHours(23);
    to.setMinutes(59);
    to.setSeconds(59);
    to.setMilliseconds(999);
    range.value.to = to;
    range.value.seconds = 0;

    _from.value = dateToDateTimeString(range.value.from);
    _to.value = dateToDateTimeString(range.value.to);

    isCustomRange.value = true;
    isValidFrom.value = true;
    isValidTo.value = true;
  };
  
  // Set predefined range
  const setRange = (index) => {
    if (typeof index === "undefined") return;
    
    const predefined = PREDEFINED_RANGES[index];
    isCustomRange.value = false;
    
    if (useStringFormat) {
      // Use the predefined from/to values
      range.value.from = predefined.from;
      range.value.to = predefined.to;
    } else {
      const now = new Date();
      range.value.from = new Date(now.getTime() - predefined.value * 1000);
      range.value.to = now;
      range.value.seconds = predefined.value;

      _from.value = dateToDateTimeString(range.value.from);
      _to.value = dateToDateTimeString(range.value.to);
    }
    
    // Update inputs
    isValidFrom.value = true;
    isValidTo.value = true;
  };
  
  return {
    predefinedRangeIdx,
    pickerDates,
    isCustomRange,
    fromInput,
    toInput,
    isValidFrom,
    isValidTo,
    expressionToDate,
    handlePickerInput,
    setRange,
    updateFromValue,
    updateToValue,
    formatRangeValue
  };
}
